import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import uniqBy from 'lodash/uniqBy';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	type ActivityItemEdge,
	type AggError,
	CancellablePromise,
	type Container,
	fetchOutboundAuthData,
	type Maybe,
	type OAuthServiceAccount,
	type OAuthSetup,
	type SearchPageNode,
} from '@atlassian/search-client';
import { getEnvironment } from '@atlassian/search-common';
import {
	getMutuallyExclusiveUIExperiment,
	STATSIG_QUICK_FIND_LAYER,
} from '@atlassian/search-experiment';

import { ProductKeys3P } from '../../../common/constants/3p-product-configs';
import {
	getUppercasePrimaryProductKey,
	is3pProductKeyForAnalytics,
	isProductKey,
	PrimaryProductKeys,
	type ProductKeys,
} from '../../../common/constants/products';
import { SearchExperience } from '../../../common/constants/search';
import { getSessionIdFromLocalStorage } from '../../../common/utils/events/utils';
import {
	onTaskFail,
	onTaskStart,
	onTaskSuccess,
	TASK_NAMES,
} from '../../../common/utils/events/vital-stats-events';
import { serveGithubLinksHydrationWithoutExposure } from '../../../common/utils/experiment';
import {
	serveQFAppSelectorExperiment,
	serveQFDropdownExperiment,
} from '../../../common/utils/experiments';
import { filterSearchResults } from '../../../common/utils/filter-search-results';
import { measureAsyncFunction } from '../../../common/utils/measure-duration';
import {
	getIsBulkConnectBannerShown,
	getProductsInResults,
	getResultsShown,
	getResultTypeCounts,
	getSectionsAndResultsCount,
} from '../../../common/utils/quick-find/analytics';
import { onQuickFindOperational } from '../../../common/utils/quick-find/events/quick-find-operational';
import { onSearchResultsShown } from '../../../common/utils/quick-find/events/search-results-shown';
import { State } from '../../../common/utils/quick-find/state';
import { isResultSmartLink } from '../../../common/utils/third-party-utils';
import { useDebouncedCallback } from '../../../common/utils/use-debounced-callback';
// eslint-disable-next-line @atlassian/tangerine/import/no-restricted-paths
import { mapActivityToSearchResult } from '../../../ui/quick-find/dialog/recent-activity';
import { useLogException } from '../../error-boundary';
import { useSelectedFilters } from '../../filters/selected';
import {
	useBootstrap,
	useIsSearchReady,
	useRequestedBackendExperiments,
	useRetryCount,
	useSearchActions,
	useSearchSessionId,
	useSelectedProduct,
	useTypeEntities,
} from '../../store';
import {
	useBrowserHistoryItems,
	useUserRelevantDocumentsExperimentEnabled,
	useUserShardModel,
	useUserShardUI,
} from '../../store/bootstrap';
import {
	useContent,
	useCurrentQueryFilter,
	useQuickFindActions,
	useQuickFindOpen,
	useQuickFindQuery,
	useSearchErrors,
	useSelectedQueryFilters,
} from '../../store/quick-find';
import {
	getCommonQueryAttributes,
	useQuickFindAnalytics,
	useQuickFindAttributes,
} from '../analytics';

import { fetchRecentActivities, resolveGithubRecentActivities } from './activities';
import { DEBOUNCE_TIMEOUT, useQuickFindResultsCallbacks } from './callbacks';
import { fetchPeopleAndTeamsResults } from './people-and-teams';
import {
	fetchAssetsEntities,
	fetchAtlasEntities,
	fetchConfluenceAnd3PEntities,
	fetchConfluenceSpaces,
	fetchJiraEntities,
	fetchJiraIssuesAndTypeEntities,
	fetchMercuryEntities,
	fetchTalentEntities,
} from './product-entities';
import { getRecentQueries } from './recent-queries';
import {
	type BrowserHistoryActivities,
	containsSearchParameters,
	getQuickFindContent,
	type PeopleAndTeams,
	type QuickFindResult,
	type RecentActivities,
	type RecentQueries,
	type SearchResults,
	type UserDocuments,
	type ZeroQueries,
	type ZeroQuery,
} from './types';
import { fetchUserDocuments } from './user-documents';
import { fetchZeroQueries } from './zero-queries';

const EMPTY_RESULTS = {
	result: [],
	duration: 0,
};

/**
 * Deduplicates arrays of items by id, keeping the first occurrence of each unique id
 * and sorts them by timestamp (most recent first)
 */
export const dedupAndSortRecentActivities = <T extends QuickFindResult<ActivityItemEdge>>(
	existingItems: T[],
	newItems: T[],
) => {
	const deduplicatedResults = uniqBy([...existingItems, ...newItems], (item) => item.id);

	// We sort to show the most recent autorefreshed results in pre-query state.
	try {
		deduplicatedResults.sort(
			(a, b) =>
				new Date(b.data.node.event.timestamp).getTime() -
				new Date(a.data.node.event.timestamp).getTime(),
		);
	} catch {
		// If we have an error, the results simply won't be sorted.
	}

	return deduplicatedResults;
};

type ResultCallbacks = {
	onPreQueryStart?: () => void;
	onPreQuerySuccess?: () => void;
	onPreQueryFailure?: (error?: Error) => void;
	onPostQueryStart?: () => void;
	onPostQuerySuccess?: (duration: number, hasThirdPartyResults: boolean) => void;
	onPostQueryFailure?: () => void;
	onPostQueryCancel?: () => void;
};

type UseQuickFindResultsOptions = {
	postQueryDisabled?: boolean;
};

const GITHUB_CONTAINER_ID_PROD =
	'c364f950-69f5-4812-9716-ed71a8d3d408_a57ca83d-597f-4de9-8dca-f9d3cd18d3a7';
const GITHUB_CONTAINER_ID_STAGING =
	'9bb160f8-1abb-4dc8-9780-ca5327e7ccc8_91300e36-3710-4f7b-aba2-95f43be1ac6f';

/**
 * Fetches search results using search parameters from the query
 * and filter stores. During and after requests, the results store is
 * updated accordingly. If multiple requests are fired only the last
 * request will cause the results store to be updated.
 */
export const useQuickFindResults = ({
	postQueryDisabled,
	onPreQueryStart,
	onPreQuerySuccess,
	onPreQueryFailure,
	onPostQueryStart,
	onPostQuerySuccess,
	onPostQueryFailure,
	onPostQueryCancel,
}: UseQuickFindResultsOptions & ResultCallbacks) => {
	const [bootstrap] = useBootstrap();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const isSearchReady = useIsSearchReady();
	const [searchSessionId] = useSearchSessionId();
	const [selectedProduct] = useSelectedProduct();
	const sessionId = getSessionIdFromLocalStorage();

	const logException = useLogException();
	const [retryCount] = useRetryCount();
	const quickFindOpen = useQuickFindOpen();
	const quickFindQuery = useQuickFindQuery();
	const currentQueryFilter = useCurrentQueryFilter();
	const selectedQueryFilters = useSelectedQueryFilters();
	const { setRequestedBackendExperiments } = useSearchActions();
	const serveUserShardUI = useUserShardUI();
	const serveUserShardModel = useUserShardModel();
	const [browserHistoryItems] = useBrowserHistoryItems();
	const { selectedFilters, entities, hasFiltersSelected } = useSelectedFilters({
		thirdPartyConfigs: bootstrap.thirdPartyConfigs,
		cloudId: bootstrap.cloudId,
		experience: SearchExperience.QUICK_FIND,
	});
	const useTypeEntitiesArgs = useMemo(
		() => ({
			thirdPartyConfigs: bootstrap.thirdPartyConfigs,
		}),
		[bootstrap.thirdPartyConfigs],
	);
	const typeFilterEntities = useTypeEntities(useTypeEntitiesArgs);
	const { fireAnalyticsEvent } = useQuickFindAnalytics();

	/**
	 * Unfortunately, we need to reintroduce this to avoid unnecessarily
	 * fetching results for the same query and filters.
	 *
	 * In order to understand why this is needed, we must first understand that
	 * the filters object is one gigantic object - whenver any kind of updates
	 * happens to the filters, the object reference changes, as it should. This
	 * includes when the loaded options in the filters change, e.g. when opening
	 * the space filter or typing into the lookup query for the filter. These
	 * user interactions cause the filters object to change, which in turn causes
	 * the references of selectedFilters and entities to change, regardless of
	 * whether or not search parameters (query, filters and entities) have changed.
	 *
	 * In the past, we were able to get away wih reacting on references changes
	 * of selectedFilters and entities as it was impossible for filters to change
	 * in Quick Find without actually changing the search parameters. Now that
	 * there are UI elements in Quick Find (e.g. space filter), this is no longer
	 * true.
	 *
	 * This is temporary until we move to search requests being triggered on user
	 * interactions, rather than state updates, which will happen as part of Relay
	 * migration.
	 */
	const selectedFiltersEquality = JSON.stringify(selectedFilters);
	const entitiesEquality = JSON.stringify(entities);

	const prevQueryRef = useRef<string>();
	const prevQuickFindOpenRef = useRef<boolean>(quickFindOpen);
	const warmupRequestWasFired = useRef<boolean>(false);
	const onPreQuerySuccessWasFired = useRef<boolean>(false);
	const prevSelectedFiltersEquality = useRef<string>();
	const prevEntitiesEquality = useRef<string>();
	const prevHasFiltersSelected = useRef<boolean>(hasFiltersSelected);
	const fetchRecentActivitiesPromiseRef = useRef<Promise<RecentActivities>>();
	const fetchUserDocumentsPromiseRef = useRef<Promise<UserDocuments>>();
	const fetchGithubActivitiesPromiseRef = useRef<Promise<any>>();
	const githubActivitiesResultRef = useRef<RecentActivities>();
	const preQueryPromiseRef = useRef<CancellablePromise<[RecentActivities, UserDocuments]>>();
	const postQueryPromiseRef = useRef<CancellablePromise<any>>();
	const prevSelectedProduct = useRef<ProductKeys | undefined>(selectedProduct);
	const outboundAuthCheckPromiseRef = useRef<Promise<boolean>>();

	const {
		setUpdateRecentQueries,
		setFetchEntitiesRequestId,
		setFetchEntitiesTraceId,
		setUpdateZeroQueries,
		setFetchSpacesRequestId,
		setSearchErrors,
	} = useQuickFindActions();

	const [preQueryLoading, setPreQueryLoading] = useState(true);
	const [preQueryError, setPreQueryError] = useState<Error | undefined>();

	const [postQueryLoading, setPostQueryLoading] = useState(false);
	const [postQueryError, setPostQueryError] = useState<Error | undefined>();

	const [recentQueries, setRecentQueries] = useState<RecentQueries>([]);
	const [filteredRecentQueries, setFilteredRecentQueries] = useState<RecentQueries>([]);
	const [filteredRecentActivities, setFilteredRecentActivities] =
		useState<RecentActivities>(EMPTY_RESULTS);
	const [filteredUserDocuments, setFilteredUserDocuments] = useState<UserDocuments>(EMPTY_RESULTS);
	const [filteredBrowserHistoryActivities, setFilteredBrowserHistoryActivities] =
		useState<BrowserHistoryActivities>(EMPTY_RESULTS);
	const [filteredZeroQueries, setFilteredZeroQueries] = useState<ZeroQuery[]>([]);
	const [zeroQueries, setZeroQueries] = useState<ZeroQueries>(EMPTY_RESULTS);
	const [searchResults, setSearchResults] = useState<SearchResults>(EMPTY_RESULTS);
	const [peopleAndTeams, setPeopleAndTeams] = useState<PeopleAndTeams>(EMPTY_RESULTS);
	const serveQFAppSelector = serveQFAppSelectorExperiment(bootstrap.primaryProduct, {
		fireExperimentExposure: false,
	});

	const serveZeroQuery =
		((bootstrap.primaryProduct === PrimaryProductKeys.Confluence ||
			bootstrap.primaryProduct === PrimaryProductKeys.Atlas) &&
			fg('kd_zero_query_in_qf_fg')) ||
		(bootstrap.primaryProduct === PrimaryProductKeys.Jira && fg('jira_search_qf_zero_queries'));

	const serveBrowserActivityExperiment = useMemo(() => {
		const cohort = FeatureGates.getExperimentValue<string>(
			'rovo-ext_browser-activity',
			'cohort',
			'control',
			{ fireExperimentExposure: false },
		);
		return cohort === 'test';
	}, []);

	const shouldUseBrowserHistory = browserHistoryItems?.length && serveBrowserActivityExperiment;

	const processBrowserHistoryItems = useCallback(
		(query: string) => {
			if (!browserHistoryItems?.length) {
				return { result: [] };
			}

			const historyItemsForFiltering = browserHistoryItems.map((item) => ({
				title: item.title,
				data: item,
			}));

			const filteredItems = filterSearchResults(historyItemsForFiltering, query);

			return {
				result: filteredItems.map((item, index) => ({
					id: `browser-history-${index}`,
					title: item.title,
					data: item.data,
					type: 'browser-history',
				})),
			};
		},
		[browserHistoryItems],
	);

	const [userRelevantDocumentsExperimentEnabled] = useUserRelevantDocumentsExperimentEnabled();

	const { serveAutorefreshCohort } = useMemo(() => {
		const cohort = FeatureGates.getExperimentValue<string>(
			'quick_find_autorefresh_experiment_v3',
			'cohort',
			'control',
			// We need to know the cohort in order to refresh the activities query, but we are NOT triggering the exposure
			// event here since we should be doing that every time the search dialog opens (not every time the hook re-renders)
			{ fireExperimentExposure: false },
		);

		return {
			serveAutorefreshCohort: cohort === 'autorefresh',
		};
	}, []);

	/**
	 * Callback to update recent queries for the current product and user
	 */
	const fetchRecentQueries = useCallback(() => {
		if (!bootstrap.primaryProduct || !bootstrap.user?.id) {
			return;
		}

		const recentQueries = getRecentQueries(bootstrap.primaryProduct, bootstrap.user.id);
		setRecentQueries(recentQueries);
	}, [bootstrap.primaryProduct, bootstrap.user?.id, setRecentQueries]);

	/**
	 * Store the callback to update recent queries in the store. This is used to refresh recent queries
	 * when they are updated, e.g. when a search request is made or when a recent query is removed.
	 */
	useEffect(() => {
		fetchRecentQueries();
		setUpdateRecentQueries(fetchRecentQueries);
	}, [fetchRecentQueries, setUpdateRecentQueries]);

	/**
	 * Callback to fetch recent activities
	 */
	const fetchRecentActivitiesCallback = useCallback(
		async (cursor?: string) => {
			if (!bootstrap.cloudId) {
				return [];
			}

			if (!fg('rovo-search-vital-stats-for-recents')) {
				return fetchRecentActivities(
					bootstrap.cloudId,
					bootstrap.aggAbsoluteUrl,
					bootstrap.experimentConfig,
					cursor,
				);
			}

			const taskId = crypto.randomUUID();
			const taskStartAt = performance.now();
			const baseVitalStatsParams = {
				searchSessionId,
				taskId,
				task: TASK_NAMES.QUICK_FIND_RECENT_ACTIVITIES_UX,
			};

			fireAnalyticsEvent(onTaskStart(baseVitalStatsParams));

			try {
				const recentActivities = await fetchRecentActivities(
					bootstrap.cloudId,
					bootstrap.aggAbsoluteUrl,
					bootstrap.experimentConfig,
					cursor,
				);

				// It doesn't distinguish between a network call and fetching from cache.
				// We can consolidate that once quick_find_autorefresh_experiment_v3 is shipped.
				fireAnalyticsEvent(
					onTaskSuccess({
						...baseVitalStatsParams,
						moreContext: {
							timeTaken: performance.now() - taskStartAt,
							resultsLoaded: recentActivities.length,
							resultsWithFailedHydration: recentActivities
								.filter((activity) => activity.data?.node?.object?.data === null)
								.map((activity) => activity.id),
						},
					}),
				);
				return recentActivities;
			} catch (error) {
				fireAnalyticsEvent(
					onTaskFail({
						...baseVitalStatsParams,
						moreContext: {
							error: error instanceof Error ? error.message : String(error),
							timeTaken: performance.now() - taskStartAt,
						},
					}),
				);
				throw error;
			}
		},
		[
			bootstrap.cloudId,
			bootstrap.aggAbsoluteUrl,
			bootstrap.experimentConfig,
			searchSessionId,
			fireAnalyticsEvent,
		],
	);

	const fetchUserDocumentsCallback = useCallback(async () => {
		if (bootstrap.cloudId && bootstrap.user?.id && bootstrap.primaryProduct === 'confluence') {
			return fetchUserDocuments(bootstrap.cloudId, bootstrap.user.id, bootstrap.aggAbsoluteUrl);
		} else {
			return [];
		}
	}, [bootstrap.cloudId, bootstrap.user?.id, bootstrap.aggAbsoluteUrl, bootstrap.primaryProduct]);

	/**
	 * Callback to fetch zero queries
	 */
	const fetchZeroQueriesCallback = useCallback(() => {
		if (!bootstrap.cloudId) {
			return;
		}
		measureAsyncFunction(() =>
			fetchZeroQueries({
				cloudId: bootstrap.cloudId!,
				...((fg('jira_search_qf_zero_queries') || fg('search_qf_query_suggestions')) && {
					product: getUppercasePrimaryProductKey(bootstrap.primaryProduct),
				}),
			}),
		)
			.then((result) => {
				setZeroQueries(result ?? EMPTY_RESULTS);
			})
			.catch((error: Error) => {
				logException(error);
				setZeroQueries(EMPTY_RESULTS);
			});
	}, [bootstrap.cloudId, logException, bootstrap.primaryProduct]);

	/**
	 * Store the callback to update zero queries in the store. This is used to refresh zero queries
	 * when they are updated, e.g. when a zero query is clicked.
	 */
	useEffect(() => {
		if (serveZeroQuery) {
			fetchZeroQueriesCallback();
		}
		setUpdateZeroQueries(fetchZeroQueriesCallback);
	}, [fetchZeroQueriesCallback, setUpdateZeroQueries, serveZeroQuery]);

	const serveGithubLinksHydrationCohort = useMemo(() => {
		const cohort = FeatureGates.getExperimentValue<string>(
			'enable_github_links_hydration_in_quickfind_recent',
			'cohort',
			'control',
			{ fireExperimentExposure: false },
		);

		return cohort === 'test';
	}, []);

	const checkGithubSmartLinkAccess = useCallback(async (): Promise<boolean> => {
		if (outboundAuthCheckPromiseRef.current) {
			return outboundAuthCheckPromiseRef.current;
		}

		const aaid = bootstrap.user?.id;

		if (!aaid) {
			return false;
		}

		const checkPromise = (async () => {
			try {
				const data = await fetchOutboundAuthData(aaid);

				const targetContainerId =
					getEnvironment() === 'production'
						? GITHUB_CONTAINER_ID_PROD
						: GITHUB_CONTAINER_ID_STAGING;

				return !!data?.containers
					?.find((c: Container) => c?.container?.containerId === targetContainerId)
					?.services?.find((s: OAuthSetup) => s?.service?.serviceKey === 'github')
					?.accounts?.some((a: OAuthServiceAccount) => a?.status === 'available');
			} catch (error) {
				logException(error instanceof Error ? error : new Error(String(error)));
				return false;
			}
		})();

		outboundAuthCheckPromiseRef.current = checkPromise;
		return checkPromise;
	}, [bootstrap.user?.id, logException]);

	const fetchGithubActivitiesCallback = useCallback(
		async (recentActivities: RecentActivities) => {
			const githubConfig = bootstrap.thirdPartyConfigs?.[ProductKeys3P.Github];
			const githubFullConnectorAuthorised = githubConfig && !githubConfig.userNeedsOAuth;

			if (!serveGithubLinksHydrationCohort || fetchGithubActivitiesPromiseRef.current) {
				return;
			}

			fetchGithubActivitiesPromiseRef.current = (async () => {
				if (githubFullConnectorAuthorised || (await checkGithubSmartLinkAccess())) {
					measureAsyncFunction(() =>
						resolveGithubRecentActivities(recentActivities, bootstrap.aggAbsoluteUrl),
					)
						.then(({ result: githubEdges, duration }) => {
							githubActivitiesResultRef.current = {
								result: githubEdges,
								duration: duration,
							};
						})
						.catch((error) => {
							logException(error);
							githubActivitiesResultRef.current = EMPTY_RESULTS;
						});

					FeatureGates.manuallyLogExperimentExposure(
						'enable_github_links_hydration_in_quickfind_recent',
					);
				} else {
					githubActivitiesResultRef.current = EMPTY_RESULTS;
				}
			})();
		},
		[
			bootstrap.aggAbsoluteUrl,
			logException,
			serveGithubLinksHydrationCohort,
			bootstrap.thirdPartyConfigs,
			checkGithubSmartLinkAccess,
		],
	);

	/**
	 * Callback to fetch pre-query results and updates the store with the results
	 */
	const fetchQuickFindPreQuery = useCallback(
		(
			query: string,
			hideFilteredPreQueryResults: boolean,
			shouldRefetchPreQueryResults: boolean,
		) => {
			if (hideFilteredPreQueryResults) {
				setFilteredRecentQueries([]);
				setFilteredRecentActivities(EMPTY_RESULTS);
				setFilteredBrowserHistoryActivities(EMPTY_RESULTS);
				setFilteredZeroQueries([]);
				userRelevantDocumentsExperimentEnabled && setFilteredUserDocuments(EMPTY_RESULTS);
			}

			if (!fetchRecentActivitiesPromiseRef.current || !fetchUserDocumentsPromiseRef.current) {
				setPreQueryError(undefined);
				setPreQueryLoading(true);
				onPreQueryStart?.();
			}

			if (!fetchRecentActivitiesPromiseRef.current) {
				const fetchRecentActivitiesPromise = measureAsyncFunction(
					fetchRecentActivitiesCallback,
				).catch((error: Error) => {
					logException(error);
					setPreQueryError(error);
					onPreQueryFailure?.(error);

					fetchRecentActivitiesPromiseRef.current = undefined;

					return EMPTY_RESULTS;
				});

				fetchRecentActivitiesPromiseRef.current = fetchRecentActivitiesPromise;
			}

			if (shouldRefetchPreQueryResults) {
				onPreQueryStart?.();

				measureAsyncFunction(() => fetchRecentActivitiesCallback())
					.then((results) => {
						setPreQueryError(undefined);

						fetchRecentActivitiesPromiseRef.current = Promise.all([
							fetchRecentActivitiesPromiseRef.current,
							Promise.resolve(results),
						]).then(([existingResults, newResults]) => {
							const deduplicatedResults = dedupAndSortRecentActivities(
								existingResults?.result ?? [],
								newResults?.result ?? [],
							);

							return {
								result: deduplicatedResults,
								duration: newResults.duration,
							};
						});
					})
					.catch((error: Error) => {
						logException(error);
						onPreQueryFailure?.(error);
					});
			}

			if (!fetchUserDocumentsPromiseRef.current) {
				if (userRelevantDocumentsExperimentEnabled) {
					const fetchUserDocumentsPromise = measureAsyncFunction(fetchUserDocumentsCallback).catch(
						(error: Error) => {
							logException(error);
							setPreQueryError(error);
							onPreQueryFailure?.(error);

							return EMPTY_RESULTS;
						},
					);

					fetchUserDocumentsPromiseRef.current = fetchUserDocumentsPromise;
				} else {
					fetchUserDocumentsPromiseRef.current = Promise.resolve(EMPTY_RESULTS);
				}
			}

			const preQueryPromise = new CancellablePromise(
				Promise.all([
					fetchRecentActivitiesPromiseRef.current,
					fetchUserDocumentsPromiseRef.current,
				]),
			);

			preQueryPromiseRef.current = preQueryPromise;

			preQueryPromise
				.promise()
				.then(([recentActivities, userDocuments]) => {
					if (!onPreQuerySuccessWasFired.current) {
						onPreQuerySuccess?.();
						onPreQuerySuccessWasFired.current = true;
					}

					setPreQueryLoading(false);

					if (hideFilteredPreQueryResults) {
						return;
					}

					const filteredRecentQueries = filterSearchResults(recentQueries, query);
					setFilteredRecentQueries(filteredRecentQueries);

					if (
						bootstrap.primaryProduct === PrimaryProductKeys.Jira &&
						fg('search_qf_query_suggestions')
					) {
						// We only filter zero queries for Jira as it's needed in post-query state
						const filteredZeroQueries = filterSearchResults(zeroQueries.result, query);
						setFilteredZeroQueries(filteredZeroQueries);
					} else {
						setFilteredZeroQueries(zeroQueries.result);
					}

					fetchGithubActivitiesCallback(recentActivities);

					if (serveGithubLinksHydrationCohort && githubActivitiesResultRef.current) {
						const filteredRecentActivities = filterSearchResults(
							dedupAndSortRecentActivities(
								recentActivities.result,
								githubActivitiesResultRef.current.result,
							),
							query,
						);
						setFilteredRecentActivities({
							result: filteredRecentActivities,
							duration: recentActivities.duration + githubActivitiesResultRef.current.duration,
						});
					} else {
						const filteredRecentActivities = filterSearchResults(recentActivities.result, query);
						setFilteredRecentActivities({
							result: filteredRecentActivities,
							duration: recentActivities.duration,
						});
					}

					if (userRelevantDocumentsExperimentEnabled) {
						const filteredUserDocuments = filterSearchResults(userDocuments.result, query);
						setFilteredUserDocuments({
							result: filteredUserDocuments,
							duration: userDocuments.duration,
						});
					}

					if (shouldUseBrowserHistory) {
						setFilteredBrowserHistoryActivities(processBrowserHistoryItems(query));
					} else {
						setFilteredBrowserHistoryActivities(EMPTY_RESULTS);
					}
				})
				.catch(() => {
					// no-op on cancel
				})
				.finally(() => {
					preQueryPromiseRef.current = undefined;
				});
		},
		[
			fetchRecentActivitiesCallback,
			fetchGithubActivitiesCallback,
			fetchUserDocumentsCallback,
			logException,
			onPreQueryFailure,
			onPreQueryStart,
			onPreQuerySuccess,
			processBrowserHistoryItems,
			recentQueries,
			zeroQueries,
			userRelevantDocumentsExperimentEnabled,
			shouldUseBrowserHistory,
			bootstrap.primaryProduct,
			serveGithubLinksHydrationCohort,
		],
	);

	/**
	 * Callback to fetch confluence entities
	 */
	const fetchConfluenceEntitiesCallback = useCallback(async () => {
		return Promise.all([
			fetchConfluenceAnd3PEntities(
				bootstrap.aggAbsoluteUrl,
				quickFindQuery,
				bootstrap.primaryProduct,
				bootstrap.cloudId || '',
				searchSessionId,
				selectedFilters,
				bootstrap.experimentConfig,
				bootstrap.productEdition,
				bootstrap.productDataRegions,
				setRequestedBackendExperiments,
				entities,
				bootstrap.thirdPartyConfigs,
				sessionId,
			),
			fetchConfluenceSpaces(
				bootstrap.aggAbsoluteUrl,
				quickFindQuery,
				bootstrap.primaryProduct,
				bootstrap.cloudId || '',
				searchSessionId,
				selectedFilters,
				bootstrap.experimentConfig,
				bootstrap.productEdition,
				bootstrap.productDataRegions,
				bootstrap.thirdPartyConfigs,
				sessionId,
			),
		]).then((results) => {
			const [entities, spaces] = results;
			return {
				results: [...entities.fetchResults, ...spaces.fetchResults],
				fetchEntitiesRequestId: entities.fetchEntitiesRequestId ?? undefined,
				fetchEntitiesTraceId: entities.fetchEntitiesTraceId ?? undefined,
				fetchSpacesRequestId: spaces.fetchSpacesRequestId ?? undefined,
				errors: [...(entities.errors || []), ...(spaces.errors || [])],
			};
		});
	}, [
		bootstrap.aggAbsoluteUrl,
		bootstrap.primaryProduct,
		bootstrap.cloudId,
		bootstrap.experimentConfig,
		bootstrap.productEdition,
		bootstrap.productDataRegions,
		bootstrap.thirdPartyConfigs,
		quickFindQuery,
		searchSessionId,
		selectedFilters,
		setRequestedBackendExperiments,
		entities,
		sessionId,
	]);

	/**
	 * Callback to fetch atlas entities
	 */
	const fetchAtlasEntitiesCallback = useCallback(
		async () =>
			fetchAtlasEntities(
				bootstrap.aggAbsoluteUrl,
				quickFindQuery,
				bootstrap.primaryProduct,
				bootstrap.cloudId || '',
				searchSessionId,
				selectedFilters,
				bootstrap.experimentConfig,
				bootstrap.productEdition,
				bootstrap.productDataRegions,
				setRequestedBackendExperiments,
				bootstrap.thirdPartyConfigs,
				sessionId,
			).then((results) => {
				const { fetchResults, fetchEntitiesRequestId, fetchEntitiesTraceId, errors } = results;
				return {
					results: fetchResults,
					fetchEntitiesRequestId: fetchEntitiesRequestId ?? undefined,
					fetchEntitiesTraceId: fetchEntitiesTraceId ?? undefined,
					fetchSpacesRequestId: undefined,
					errors,
				};
			}),
		[
			bootstrap.aggAbsoluteUrl,
			bootstrap.primaryProduct,
			bootstrap.cloudId,
			bootstrap.experimentConfig,
			bootstrap.productEdition,
			bootstrap.productDataRegions,
			bootstrap.thirdPartyConfigs,
			quickFindQuery,
			searchSessionId,
			selectedFilters,
			setRequestedBackendExperiments,
			sessionId,
		],
	);

	/**
	 * Callback to fetch assets entities
	 */
	const fetchAssetsEntitiesCallback = useCallback(
		async () =>
			fetchAssetsEntities(
				bootstrap.aggAbsoluteUrl,
				quickFindQuery,
				bootstrap.primaryProduct,
				bootstrap.cloudId || '',
				searchSessionId,
				selectedFilters,
				bootstrap.experimentConfig,
				bootstrap.productEdition,
				bootstrap.productDataRegions,
				setRequestedBackendExperiments,
				bootstrap.thirdPartyConfigs,
				sessionId,
			).then((results) => {
				const { fetchResults, fetchEntitiesRequestId, fetchEntitiesTraceId, errors } = results;
				return {
					results: fetchResults,
					fetchEntitiesRequestId: fetchEntitiesRequestId ?? undefined,
					fetchEntitiesTraceId: fetchEntitiesTraceId ?? undefined,
					fetchSpacesRequestId: undefined as string | undefined,
					errors,
				};
			}),
		[
			bootstrap.aggAbsoluteUrl,
			bootstrap.primaryProduct,
			bootstrap.cloudId,
			bootstrap.experimentConfig,
			bootstrap.productEdition,
			bootstrap.productDataRegions,
			bootstrap.thirdPartyConfigs,
			quickFindQuery,
			searchSessionId,
			selectedFilters,
			setRequestedBackendExperiments,
			sessionId,
		],
	);

	/**
	 * Callback to fetch confluence entities
	 */
	const fetchMercuryEntitiesCallback = useCallback(
		async () =>
			fetchMercuryEntities(
				bootstrap.aggAbsoluteUrl,
				quickFindQuery,
				bootstrap.primaryProduct,
				bootstrap.cloudId || '',
				searchSessionId,
				selectedFilters,
				bootstrap.experimentConfig,
				bootstrap.productEdition,
				bootstrap.productDataRegions,
				setRequestedBackendExperiments,
				bootstrap.thirdPartyConfigs,
				sessionId,
			).then((results) => {
				const { fetchResults, fetchEntitiesRequestId, fetchEntitiesTraceId, errors } = results;
				return {
					results: [...fetchResults],
					fetchEntitiesRequestId: fetchEntitiesRequestId ?? undefined,
					fetchEntitiesTraceId: fetchEntitiesTraceId ?? undefined,
					fetchSpacesRequestId: undefined as string | undefined,
					errors,
				};
			}),
		[
			bootstrap.aggAbsoluteUrl,
			bootstrap.primaryProduct,
			bootstrap.cloudId,
			bootstrap.experimentConfig,
			bootstrap.productEdition,
			bootstrap.productDataRegions,
			bootstrap.thirdPartyConfigs,
			quickFindQuery,
			searchSessionId,
			selectedFilters,
			setRequestedBackendExperiments,
			sessionId,
		],
	);

	/**
	 * Callback to fetch talent entities
	 */
	const fetchTalentEntitiesCallback = useCallback(
		async () =>
			fetchTalentEntities(
				bootstrap.aggAbsoluteUrl,
				quickFindQuery,
				bootstrap.primaryProduct,
				bootstrap.cloudId || '',
				searchSessionId,
				selectedFilters,
				bootstrap.experimentConfig,
				bootstrap.productEdition,
				bootstrap.productDataRegions,
				setRequestedBackendExperiments,
				bootstrap.thirdPartyConfigs,
				sessionId,
			).then((results) => {
				const { fetchResults, fetchEntitiesRequestId, fetchEntitiesTraceId, errors } = results;
				return {
					results: [...fetchResults],
					fetchEntitiesRequestId: fetchEntitiesRequestId ?? undefined,
					fetchEntitiesTraceId: fetchEntitiesTraceId ?? undefined,
					fetchSpacesRequestId: undefined as string | undefined,
					errors,
				};
			}),
		[
			bootstrap.aggAbsoluteUrl,
			bootstrap.primaryProduct,
			bootstrap.cloudId,
			bootstrap.experimentConfig,
			bootstrap.productEdition,
			bootstrap.productDataRegions,
			bootstrap.thirdPartyConfigs,
			quickFindQuery,
			searchSessionId,
			selectedFilters,
			setRequestedBackendExperiments,
			sessionId,
		],
	);

	/**
	 *  Callback to fetch jira entities
	 */
	const fetchJiraIssuesCallback = useCallback(async () => {
		return Promise.all([
			fetchJiraIssuesAndTypeEntities(
				bootstrap.aggAbsoluteUrl,
				quickFindQuery,
				bootstrap.primaryProduct,
				bootstrap.cloudId || '',
				searchSessionId,
				selectedFilters,
				bootstrap.experimentConfig,
				bootstrap.productEdition,
				bootstrap.productDataRegions,
				setRequestedBackendExperiments,
				bootstrap.isJiraIssueIndexed,
				bootstrap.thirdPartyConfigs,
				typeFilterEntities,
				sessionId,
				bootstrap.hasEditOrViewPlanPermission,
			),
			!hasFiltersSelected
				? fetchJiraEntities(
						bootstrap.aggAbsoluteUrl,
						quickFindQuery,
						bootstrap.primaryProduct,
						bootstrap.cloudId || '',
						searchSessionId,
						selectedFilters,
						bootstrap.experimentConfig,
						bootstrap.productEdition,
						bootstrap.productDataRegions,
						setRequestedBackendExperiments,
						bootstrap.isJiraIssueIndexed,
						bootstrap.thirdPartyConfigs,
						sessionId,
						bootstrap.hasEditOrViewPlanPermission,
					)
				: Promise.resolve({ fetchResults: [], errors: [] }),
		]).then((results) => {
			const [issuesResult, entitiesResult] = results;

			return {
				results: [...issuesResult.fetchResults, ...entitiesResult.fetchResults],
				fetchEntitiesRequestId: issuesResult.fetchEntitiesRequestId ?? undefined,
				fetchEntitiesTraceId: issuesResult.fetchEntitiesTraceId ?? undefined,
				fetchSpacesRequestId: undefined,
				errors: [...(issuesResult.errors || []), ...(entitiesResult.errors || [])],
			};
		});
	}, [
		bootstrap.aggAbsoluteUrl,
		bootstrap.primaryProduct,
		bootstrap.cloudId,
		bootstrap.experimentConfig,
		bootstrap.productEdition,
		bootstrap.productDataRegions,
		bootstrap.isJiraIssueIndexed,
		bootstrap.thirdPartyConfigs,
		bootstrap.hasEditOrViewPlanPermission,
		quickFindQuery,
		searchSessionId,
		selectedFilters,
		setRequestedBackendExperiments,
		typeFilterEntities,
		hasFiltersSelected,
		sessionId,
	]);

	/**
	 * Callback to fetch people and teams results
	 */
	const fetchPeopleAndTeamsCallback = useCallback(async () => {
		const { orgId, primaryProduct, user, cloudId } = bootstrap;
		if (orgId && primaryProduct && user?.id && cloudId) {
			// When a single query filter is selected and it is a contributor filter,
			// get people and teams results using the contributor name
			if (selectedQueryFilters.length === 1 && selectedQueryFilters[0].filterId === 'contributor') {
				return fetchPeopleAndTeamsResults(
					selectedQueryFilters[0].displayText,
					orgId,
					primaryProduct,
					user.id,
					cloudId,
				);
			}

			// Only fetch people and teams results if there are no filters selected
			if (!hasFiltersSelected) {
				return fetchPeopleAndTeamsResults(quickFindQuery, orgId, primaryProduct, user.id, cloudId);
			}
		}

		return [];
	}, [bootstrap, quickFindQuery, selectedQueryFilters, hasFiltersSelected]);

	/**
	 * Function which fetches the post query results. This consists of a call to get
	 * product search results and a call to get people and teams. Given that Promise.allSettled
	 * is used, the slowest request will determine the time it takes to resolve.
	 */
	const fetchQuickFindPostQuery = useCallback(() => {
		let fetchProductEntities;
		if (bootstrap.primaryProduct === 'confluence') {
			fetchProductEntities = fetchConfluenceEntitiesCallback;
		} else if (bootstrap.primaryProduct === 'townsquare') {
			fetchProductEntities = fetchAtlasEntitiesCallback;
		} else if (bootstrap.primaryProduct === 'mercury') {
			fetchProductEntities = fetchMercuryEntitiesCallback;
		} else if (bootstrap.primaryProduct === 'radar' && fg('talent_global_search')) {
			fetchProductEntities = fetchTalentEntitiesCallback;
		} else if (bootstrap.primaryProduct === 'jira') {
			fetchProductEntities = fetchJiraIssuesCallback;
		} else if (bootstrap.primaryProduct === 'cmdb' && fg('assets_rovo_search_all')) {
			fetchProductEntities = fetchAssetsEntitiesCallback;
		} else {
			fetchProductEntities = () =>
				Promise.resolve({
					results: [],
					fetchEntitiesRequestId: undefined,
					fetchEntitiesTraceId: undefined,
					fetchSpacesRequestId: undefined,
					errors: [],
				});
		}

		const fetchPeopleAndTeams =
			bootstrap.primaryProduct !== 'jira' &&
			bootstrap.primaryProduct !== 'mercury' &&
			!selectedProduct
				? fetchPeopleAndTeamsCallback
				: () => Promise.resolve([]);

		const postQueryPromise = new CancellablePromise(
			Promise.allSettled([
				measureAsyncFunction(fetchProductEntities),
				measureAsyncFunction(fetchPeopleAndTeams),
			]),
		);
		postQueryPromiseRef.current = postQueryPromise;

		onPostQueryStart?.();

		postQueryPromise
			.promise()
			.then((results) => {
				const productEntitiesStatus = results[0].status;
				const peopleAndTeamsStatus = results[1].status;

				// Post-query success is determined by product entities request, errors
				// from people and teams are ignored
				if (productEntitiesStatus === 'fulfilled') {
					const fetchedResults = results[0].value.result.results;
					setSearchResults({
						result: fetchedResults,
						duration: results[0].value.duration,
					});

					const errors = results[0].value.result.errors || [];
					setSearchErrors(errors);

					const hasThirdPartyResults = fetchedResults.some(
						(result) =>
							result.id.startsWith('ari:cloud:graph::document') ||
							result.id.startsWith('ari:cloud:graph::remote-link'),
					);

					setFetchEntitiesRequestId(results[0].value.result.fetchEntitiesRequestId);
					setFetchEntitiesTraceId(results[0].value.result.fetchEntitiesTraceId);
					setFetchSpacesRequestId(results[0].value.result.fetchSpacesRequestId);

					const peopleAndTeams =
						peopleAndTeamsStatus === 'fulfilled'
							? {
									result: results[1].value.result,
									duration: results[1].value.duration,
								}
							: EMPTY_RESULTS;
					setPeopleAndTeams(peopleAndTeams);

					if (shouldUseBrowserHistory) {
						setFilteredBrowserHistoryActivities(processBrowserHistoryItems(quickFindQuery));
					} else {
						setFilteredBrowserHistoryActivities(EMPTY_RESULTS);
					}

					const allDurations = results.map((result) =>
						result.status === 'fulfilled' ? result.value.duration : 0,
					);
					const maxDuration = Math.max(...allDurations);

					onPostQuerySuccess?.(maxDuration, hasThirdPartyResults);
				} else {
					setPostQueryError(new Error('Failed to fetch post query results'));
					onPostQueryFailure?.();
				}

				setPostQueryLoading(false);
				return results;
			})
			.catch(() => {
				// Promise.allSettled will never reject, hence rejections only occur
				// when the promise is cancelled. In this case, we don't need to do anything.
			});
	}, [
		bootstrap,
		fetchAssetsEntitiesCallback,
		fetchAtlasEntitiesCallback,
		fetchConfluenceEntitiesCallback,
		fetchJiraIssuesCallback,
		fetchMercuryEntitiesCallback,
		fetchTalentEntitiesCallback,
		fetchPeopleAndTeamsCallback,
		onPostQueryFailure,
		onPostQueryStart,
		onPostQuerySuccess,
		processBrowserHistoryItems,
		quickFindQuery,
		selectedProduct,
		setFetchEntitiesRequestId,
		setFetchEntitiesTraceId,
		setFetchSpacesRequestId,
		shouldUseBrowserHistory,
		setSearchErrors,
	]);

	/**
	 * Debounced fetch function for quick find results. This callback should never mutate
	 * as the only dependency is the timeout value, hence it can be safely used in dependency array.
	 */
	const debouncedFetchQuickFindPostQuery = useDebouncedCallback(
		fetchQuickFindPostQuery,
		DEBOUNCE_TIMEOUT,
	);

	/**
	 * Warm-up request for user shard permissions cache when dialog is opened **for the first time**
	 * The user shard team has an idea to reduce 3p latency which is to pre-fetch documents and run them through LMC when the dialog opens.
	 * Queries that are not empty will be checked against those cached values, as a way to remove LMC latency from QF queries.
	 * The cache exists in searcher.
	 * For more information, see https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/122582/diff#comment-602538177
	 * Demo of how this works: https://www.loom.com/share/e190178ec2e048558273cd8d60a7006d
	 */
	useEffect(() => {
		if (quickFindOpen && !warmupRequestWasFired.current && serveUserShardModel) {
			debouncedFetchQuickFindPostQuery();
			warmupRequestWasFired.current = true;
		}
	}, [quickFindOpen, debouncedFetchQuickFindPostQuery, serveUserShardModel]);

	useEffect(() => {
		if (quickFindOpen) {
			FeatureGates.manuallyLogExperimentExposure('rovo-ext_browser-activity');

			// Only fire an exposure if the user is eligible for the experiment.
			// enable_quick_find_autorefresh_experiment_v3 is referenced in quick_find_autorefresh_experiment_v3.
			if (fg('enable_quick_find_autorefresh_experiment_v3')) {
				FeatureGates.manuallyLogExperimentExposure('quick_find_autorefresh_experiment_v3');
			}

			if (fg('refactor_mutually_exclusive_search_ui_layers')) {
				// If the user is allocated to either of these mutually exclusive experiments,
				// We fire an exposure by accessing a parameter within the layer.
				getMutuallyExclusiveUIExperiment(
					'quick-find-user-shard-search-experiment',
					STATSIG_QUICK_FIND_LAYER,
				)?.get('cohort', undefined);
				getMutuallyExclusiveUIExperiment(
					'quick_find_user_relevant_documents_experiment',
					STATSIG_QUICK_FIND_LAYER,
				)?.get('cohort', undefined);
			}

			serveQFDropdownExperiment(bootstrap.primaryProduct);
			serveQFAppSelectorExperiment(bootstrap.primaryProduct);
		}
	}, [quickFindOpen, bootstrap.thirdPartyConfigs, bootstrap.primaryProduct]);

	/**
	 * Updates results when various Quick Find states change, this includes:
	 * - when search is ready
	 * - when the dialog opens
	 * - when the query changes
	 * - when recent queries or activities change
	 * - when selected entities or filters change
	 */
	useEffect(() => {
		if (!isSearchReady) {
			return;
		}

		if (preQueryPromiseRef.current) {
			preQueryPromiseRef.current.cancel();
		}

		const oldHideFilteredPreQueryResults = !!(
			hasFiltersSelected ||
			(quickFindQuery.length && selectedProduct)
		);

		const newHideFilteredPreQueryResults = !!(hasFiltersSelected || selectedProduct);

		const hideFilteredPreQueryResults = serveQFAppSelector
			? newHideFilteredPreQueryResults
			: oldHideFilteredPreQueryResults;
		// As using serveUserShardUI, we will fusion the matched results from recent activities
		// and the results from User Shard Index. So we do not hide prequery results.
		// || (quickFindQuery.length && serveUserShardUI)

		const shouldRefetchPreQueryResults =
			serveAutorefreshCohort && prevQuickFindOpenRef.current === false && quickFindOpen === true;

		fetchQuickFindPreQuery(
			quickFindQuery,
			hideFilteredPreQueryResults,
			shouldRefetchPreQueryResults,
		);

		prevQuickFindOpenRef.current = quickFindOpen;

		/**
		 * Skip fetching post-query results if:
		 * - Quick Find is closed
		 * - Post-query is disabled
		 * - There is a current query filter selected
		 */
		if (!quickFindOpen || postQueryDisabled || currentQueryFilter) {
			return;
		}

		// Check if search parameters have changed, if not, skip fetching post-query results
		if (
			quickFindQuery === prevQueryRef.current &&
			selectedFiltersEquality === prevSelectedFiltersEquality.current &&
			entitiesEquality === prevEntitiesEquality.current &&
			prevHasFiltersSelected.current === hasFiltersSelected &&
			selectedProduct === prevSelectedProduct.current
		) {
			return;
		}

		prevQueryRef.current = quickFindQuery;
		prevSelectedFiltersEquality.current = selectedFiltersEquality;
		prevEntitiesEquality.current = entitiesEquality;
		prevHasFiltersSelected.current = hasFiltersSelected;
		prevSelectedProduct.current = selectedProduct;

		// Reset post-query results as search parameters have changed
		setSearchResults({ result: [], duration: 0 });
		setPeopleAndTeams({ result: [], duration: 0 });
		setPostQueryLoading(true);
		setPostQueryError(undefined);

		// Cancel the inflight request as search parameters have changed
		if (postQueryPromiseRef.current) {
			postQueryPromiseRef.current.cancel();
			onPostQueryCancel?.();

			postQueryPromiseRef.current = undefined;
		}

		const hasProductSelected = (serveQFAppSelector && Boolean(selectedProduct)) ?? false;
		const hasQuery = containsSearchParameters({
			query: quickFindQuery,
			hasFiltersSelected: hasFiltersSelected || hasProductSelected,
		});

		if (hasQuery) {
			setPostQueryLoading(true);
			setFilteredBrowserHistoryActivities(EMPTY_RESULTS);
			debouncedFetchQuickFindPostQuery();
		}
	}, [
		fetchQuickFindPreQuery,
		debouncedFetchQuickFindPostQuery,
		quickFindQuery,
		retryCount,
		isSearchReady,
		quickFindOpen,
		selectedFilters,
		entities,
		onPostQueryCancel,
		postQueryDisabled,
		currentQueryFilter,
		selectedProduct,
		hasFiltersSelected,
		serveUserShardUI,
		selectedFiltersEquality,
		entitiesEquality,
		userRelevantDocumentsExperimentEnabled,
		serveAutorefreshCohort,
		shouldUseBrowserHistory,
		serveQFAppSelector,
	]);
	return useMemo(
		() =>
			getQuickFindContent({
				// Using refs here to ensure new results state is returned only
				// when the various result variables in the dependency array change
				query: prevQueryRef.current,
				hasFiltersSelected: prevHasFiltersSelected.current,
				quickFindOpen: prevQuickFindOpenRef.current,
				preQueryError,
				preQueryLoading,
				postQueryError,
				postQueryLoading,
				filteredRecentQueries,
				filteredRecentActivities,
				filteredUserDocuments,
				filteredBrowserHistoryActivities,
				filteredZeroQueries,
				searchResults,
				peopleAndTeams,
				postQueryDisabled,
				primaryProduct: bootstrap.primaryProduct,
				hasProductSelected: !!prevSelectedProduct.current,
			}),
		[
			filteredRecentActivities,
			filteredBrowserHistoryActivities,
			filteredUserDocuments,
			filteredRecentQueries,
			filteredZeroQueries,
			peopleAndTeams,
			postQueryDisabled,
			postQueryError,
			postQueryLoading,
			preQueryError,
			preQueryLoading,
			searchResults,
			bootstrap.primaryProduct,
		],
	);
};

// export for testing purposes
export const useSearchResultsShownAnalytics = () => {
	const previousStateRef = useRef<State | undefined>();

	const content = useContent();
	const quickFindOpen = useQuickFindOpen();
	const [{ thirdPartyConfigs }] = useBootstrap();
	const searchErrors = useSearchErrors();

	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes } = useQuickFindAttributes();
	const [requestedBackendExperiments] = useRequestedBackendExperiments();

	const productsWithTimeoutsFromErrors: ProductKeys[] | undefined = useMemo(() => {
		const errors = searchErrors || [];

		return errors
			.filter((error: AggError) => error.extensions?.statusCode === 408)
			.map((error: AggError) => error.extensions?.integration ?? error.extensions?.product)
			.flatMap((s: Maybe<string> | undefined) => (s && isProductKey(s) ? [s] : []));
	}, [searchErrors]);

	const are3PSmartLinksInResults = useMemo(() => {
		if (!content) {
			return false;
		}

		const serveGithubLinksHydration = serveGithubLinksHydrationWithoutExposure();

		const checkIsSmartLink = (result: QuickFindResult<ActivityItemEdge | SearchPageNode>) => {
			if (!result.data?.node?.object?.id || !result.data?.node?.event?.eventType) {
				return false;
			}

			const mappedResult = mapActivityToSearchResult(
				result.data,
				'', // query not needed to obtain the `product` value
				'', // searchSessionId not needed to obtain the `product` value
				thirdPartyConfigs,
				serveGithubLinksHydration,
			);

			return isResultSmartLink({
				resultAri: result.data.node.object.id,
				thirdPartyConfigs,
				productKey: mappedResult?.analyticsMetadata.product,
				eventType: result.data.node.event.eventType,
				connectorType: result.data.connectorType,
			});
		};

		return (
			('recentActivities' in content ? content.recentActivities : []).some(checkIsSmartLink) ||
			('searchResults' in content ? content.searchResults : []).some(checkIsSmartLink)
		);
	}, [content, thirdPartyConfigs]);

	useEffect(() => {
		// Store a copy of previous state before updating the ref
		const previousState = previousStateRef.current;
		previousStateRef.current = content ? content.state : undefined;

		// Don't fire analytics event if the dialog is closed
		if (!quickFindOpen || !content) {
			return;
		}

		// Don't fire analytics event if the state hasn't changed
		if (content.state === previousState) {
			return;
		}

		// Ignore states that do not have search results
		if (
			content.state === State.PREQUERY_LOADING ||
			content.state === State.PREQUERY_ERROR ||
			content.state === State.POSTQUERY_ERROR
		) {
			if (content.state === State.PREQUERY_ERROR) {
				fireAnalyticsEvent(
					onQuickFindOperational({
						success: false,
						attributes: commonAttributes,
						resultsType: 'preQuerySearchResults',
					}),
				);
			}
			if (content.state === State.POSTQUERY_ERROR) {
				fireAnalyticsEvent(
					onQuickFindOperational({
						success: false,
						attributes: commonAttributes,
						resultsType: 'postQuerySearchResults',
					}),
				);
			}
			return;
		}

		/**
		 * Do not fire analytics event when the dialog is opened and post-query
		 * results are already returned. This is to have parity with the old
		 * quick find component.
		 *
		 * Whilst this may not be correct, this is here to ensure changes in metrics
		 * such as QSR and CTR are intentional.
		 *
		 * TODO: Discuss with DS/DE if this correct behaviour
		 */
		if (content.state === State.POSTQUERY_RESULTS && previousState === undefined) {
			return;
		}

		const resultsTypeMap = {
			PREQUERY_RESULTS: 'preQuerySearchResults' as const,
			PREQUERY_NO_RESULTS: 'preQuerySearchResults' as const,
			POSTQUERY_LOADING: 'cachedResults' as const,
			POSTQUERY_RESULTS: 'postQuerySearchResults' as const,
			POSTQUERY_NO_RESULTS: 'postQuerySearchResults' as const,
		};

		const resultsType = resultsTypeMap[content.state];

		const { sectionCount, resultCount } = getSectionsAndResultsCount();
		const resultTypeCounts = getResultTypeCounts();
		const productsInResults = getProductsInResults();
		const isBulkConnectBannerShown = getIsBulkConnectBannerShown();
		const resultsShown = getResultsShown();
		const noResultsFetched =
			content.state === State.PREQUERY_NO_RESULTS ||
			content.state === State.POSTQUERY_NO_RESULTS ||
			resultCount === 0;

		// Don't fire cachedResults event if there are no results
		if (noResultsFetched && resultsType === 'cachedResults') {
			return;
		}

		const are3pResultsShown = productsInResults.some(is3pProductKeyForAnalytics);

		const authorized3PProducts = Object.keys(thirdPartyConfigs || {}).filter(
			(product) => !thirdPartyConfigs?.[product as ProductKeys3P]?.userNeedsOAuth,
		);

		fireAnalyticsEvent(
			onSearchResultsShown({
				attributes: {
					...commonAttributes,
					sectionCount,
					resultCount,
					resultTypeCounts,
					productsInResults,
					are3pResultsShown,
					// Use `searchAndResult` to keep the schema consistent with FPS, but we don't need other attributes yet.
					searchAndResult: {
						authorized3PProducts,
					},
					resultsShown,
					isBulkConnectBannerShown,
					are3PSmartLinksInResults,
					noResultsFetched,
					...getCommonQueryAttributes(content.query),
					...requestedBackendExperiments,
					...(resultsType === 'postQuerySearchResults'
						? { productsWithTimeout: productsWithTimeoutsFromErrors || [] }
						: {}),
				},
				nonPrivacySafeAttributes: {
					query: content.query,
				},
				resultsType,
			}),
		);

		fireAnalyticsEvent(
			onQuickFindOperational({
				success: true,
				attributes: commonAttributes,
				resultsType,
			}),
		);
	}, [
		commonAttributes,
		content,
		fireAnalyticsEvent,
		quickFindOpen,
		requestedBackendExperiments,
		are3PSmartLinksInResults,
		thirdPartyConfigs,
		productsWithTimeoutsFromErrors,
	]);
};

export const QuickFindResults = memo((options: UseQuickFindResultsOptions) => {
	const callbacks = useQuickFindResultsCallbacks();

	const useQuickFindResultsArgs = useMemo(
		() => ({ ...options, ...callbacks }),
		[options, callbacks],
	);

	const content = useQuickFindResults(useQuickFindResultsArgs);
	const { setContent } = useQuickFindActions();

	useEffect(() => {
		setContent(content);
	}, [content, setContent]);

	useSearchResultsShownAnalytics();

	return null;
});
